import axios, { AxiosResponse } from "axios";
import { SERVER_URL } from "Constants";
import { ApplicationEntity } from "Models/Entities";
import { customerApplicationStatus } from "Models/Enums";
import { AxiosConfigFactory } from "Util/AxiosUtil";
import { TemplateForm } from "Views/Components/TemplateForm/TemplateFormContext";
import { EntityControllerData, PaginationParams, SearchParams, SortingParams } from "./shared";

export type CustomerApplicationListItem = {
	id?: number; // Greenfield ID
	applicationId?: string; // Brownfield ID
	reassignedUserId?: string; // Brownfield Sales Rep Customer ID

	created?: Date;
	emailSent?: boolean;
	businessName?: string;
	email?: string;
	status?: customerApplicationStatus;

	//Superuser only fields below
	creditRequested?: number;
	creditApproved?: number;
	submitted?: Date;
	accountNumber?: string;
	alerts?: number;
	reports?: number;
}

export type CustomerApplicationFilterOptions = {
	filterByCustomerApplicationTagIds?: string[];
	filterByCustomerApplicationFormIds?: string[];
	filterByStatus?: (customerApplicationStatus | 'ALL')[];
	filterByCreatedDateRange?: string[];
	filterBySubmittedDateRange?: string[];
	filterByDecisionDateRange?: string[];
	filterByUserIds?: string[];
}

export type CustomerApplicationQueryOptions = PaginationParams & SortingParams & SearchParams & CustomerApplicationFilterOptions

export async function fetchCustomerApplications(queryParams?: CustomerApplicationQueryOptions) {
	const url = `${SERVER_URL}/api/entity/CustomerApplicationEntity`;

	// filter by status
	if (queryParams?.filterByStatus) {
		queryParams.filterByStatus = queryParams.filterByStatus
			.filter(status => status !== 'ALL'); // Remove 'ALL' if present
	}

	const requestConfig = AxiosConfigFactory.createRequestConfig(queryParams);
	return await axios.get<EntityControllerData<CustomerApplicationListItem>>(url, requestConfig);
}


export type CustomerApplicationTemplateEntityQueryOptions = {
	filterByBusinessIds?: string[];
	filterByUserIds?: string[];
	filterByActive?: boolean;
}

export async function apiGetCustomerApplicationTemplate(templateId: string): Promise<AxiosResponse<ApplicationEntity>> {
	const url = `${SERVER_URL}/api/entity/ApplicationEntity/${templateId}`
	return await axios.get<ApplicationEntity>(url);
}

export async function fetchCustomerApplicationTemplates(queryParams?: CustomerApplicationTemplateEntityQueryOptions): Promise<AxiosResponse<EntityControllerData<ApplicationEntity>>> {
	const url = `${SERVER_URL}/api/entity/ApplicationEntity`;
	const requestConfig = AxiosConfigFactory.createRequestConfig(queryParams);
	return await axios.get<EntityControllerData<ApplicationEntity>>(url, requestConfig);
}

type ApplicationUser = {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	fullName: string;
};

export type CustomerApplicationForm = {
	id?: string;
	ownerId?: string;
	owner?: ApplicationUser;
	reassignedUserId?: string;
	reassignedUser?: ApplicationUser;

	customerApplicationTemplateId?: string;
	businessId?: string;
	status?: customerApplicationStatus;

	jsonSchemaSubmission?: TemplateForm;

	// business numbers
	abn?: string;
	acn?: string;
	trusteeAcn?: string;
	nzbn?: string;
	ncn?: string;
	trusteeNcn?: string;

	// applicant information
	sendToApplicant?: boolean;
	applicantFirstName?: string;
	applicantLastName?: string;
	applicantEmail?: string;
	applicantPhone?: string;
	applicantCcEmail?: string;

	uploadedDocuments?: [{ Id: string, fileId: string, fileName: string }]
	files?: File[];
	notes?: string;
}

export const CustomerApplicatnFormApplicantFields: (keyof CustomerApplicationForm)[] = [
	'applicantFirstName',
	'applicantLastName',
	'applicantPhone',
	'applicantEmail',
	'applicantCcEmail',
];

export async function apiGetCustomerApplication(id: string) {
	const url = `${SERVER_URL}/api/entity/CustomerApplicationEntity/${id}`
	return await axios.get<CustomerApplicationForm>(url);
}

type CustomerAppBusinessInformation = {
	businessName?: string;
	organisationNumber?: string;
	revenue?: number;
	liabilities?: number;
	website?: string;
	businessType?: string;
	organisationNumberIsCurrent?: string;
	otherKnownNames?: string;
}

export type CustomerApplicationSummary = {
	id: string;
	legacyId: number;
	status?: customerApplicationStatus;
	creditRequested?: number;
	creditApproved?: number;
	alerts: number;
	reports: number;
	customerApplicationTemplateId: string;
	created: Date;
	businessInformation: CustomerAppBusinessInformation;
}

export const CustomerApplicationSummaryUtil = {
	getCreditRequested: function (model: CustomerApplicationSummary) {
		return model.creditRequested ? model.creditRequested?.toLocaleString("en-AU", { style: "currency", currency: "AUD" }) : '-'
	},
	getCreditApproved: function (model: CustomerApplicationSummary) {
		return model.creditApproved ? model.creditApproved?.toLocaleString("en-AU", { style: "currency", currency: "AUD" }) : '-'
	}
}

export async function apiGetCustomerApplicationSummary(id: string): Promise<AxiosResponse<CustomerApplicationSummary>> {
	const url = `${SERVER_URL}/api/entity/CustomerApplicationEntity/${id}/summary`
	return await axios.get<CustomerApplicationSummary>(url);
}

export async function apiAddCustomerApplication(request: CustomerApplicationForm) {
	const url = `${SERVER_URL}/api/entity/CustomerApplicationEntity`;
	const formData = buildCustomerApplicationFormData(request)
	return await axios.post<CustomerApplicationForm>(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
}

export async function apiUpdateCustomerApplication(request: CustomerApplicationForm) {
	if (!request.id) {
		return;
	}

	const url = `${SERVER_URL}/api/entity/CustomerApplicationEntity`;
	const formData = buildCustomerApplicationFormData(request)
	formData.append("id", request.id);
	return await axios.put<CustomerApplicationForm>(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
}

function buildCustomerApplicationFormData(request: CustomerApplicationForm): FormData {
	// Note - have to seriarlise objects to strings and deserialise on the API for FormData types
	const formData = new FormData();
	if (request.customerApplicationTemplateId) { formData.append("customerApplicationTemplateId", request.customerApplicationTemplateId); }
	if (request.businessId) { formData.append("businessId", request.businessId) }
	if (request.ownerId) { formData.append("ownerId", request.ownerId) }
	if (request.reassignedUserId) { formData.append("reassignedUserId", request.reassignedUserId); }
	if (request.jsonSchemaSubmission) { formData.append("jsonSchemaSubmission", JSON.stringify(request.jsonSchemaSubmission)); }

	// business numbers
	if (request.abn) { formData.append("abn", request.abn); }
	if (request.acn) { formData.append("acn", request.acn); }
	if (request.trusteeAcn) { formData.append("trusteeAcn", request.trusteeAcn); }
	if (request.nzbn) { formData.append("nzbn", request.nzbn); }
	if (request.ncn) { formData.append("ncn", request.ncn); }
	if (request.trusteeNcn) { formData.append("trusteeNcn", request.trusteeNcn); }

	if (request.sendToApplicant) { formData.append("sendToApplicant", request.sendToApplicant ? "true" : "false"); }
	if (request.applicantFirstName) { formData.append("applicantFirstName", request.applicantFirstName); }
	if (request.applicantLastName) { formData.append("applicantLastName", request.applicantLastName); }
	if (request.applicantEmail) { formData.append("applicantEmail", request.applicantEmail); }
	if (request.applicantPhone) { formData.append("applicantPhone", request.applicantPhone); }
	if (request.applicantCcEmail) { formData.append("applicantCcEmail", request.applicantCcEmail); }
	if (request.notes) { formData.append("notes", request.notes); }

	request.files?.forEach(file => {
		formData.append("files", file);
	})

	return formData
}

export type CustomerApplicationTagEntityQueryOptions = {
	filterByCustomerApplicationFormIds?: string[];
}

export type CustomerApplicationTemplateTagDto = {
	id: string;
	name: string;
	customerApplicationTemplateId: string;
}

export async function fetchCustomerApplicationTemplateTags(queryParams?: CustomerApplicationTagEntityQueryOptions): Promise<AxiosResponse<EntityControllerData<CustomerApplicationTemplateTagDto>>> {
	const url = `${SERVER_URL}/api/entity/ApplicationEntity/Tag`;
	const requestConfig = AxiosConfigFactory.createRequestConfig(queryParams);
	return await axios.get<EntityControllerData<CustomerApplicationTemplateTagDto>>(url, requestConfig);
}